import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useTranslations } from 'use-intl';
import { LogOut, PanelLeftClose, PanelLeftOpen, ShieldCheck } from 'lucide-react';
import { LOCALES, type Locale } from '@/i18n';
import { localePath, swapLocale } from '@/app/locale';
import { useSession } from '@/app/session';
import { ASSISTANT, GROUPS, type NavItem } from '@/lib/nav';
import { NAV_ICONS } from './icons';

const STORE_KEY = 'mpp.nav.collapsed';

/**
 * The desktop navigation: every destination, grouped, always visible.
 *
 * A laptop has room for a sidebar, and the horizontal row it replaces was
 * costing a professional coach the same seven hidden destinations it cost a
 * beginner — the old shell rendered one nav at every width.
 *
 * It collapses to a rail of icons, remembered per browser, because at 1024px a
 * 240px column plus a squad matrix is tight and the coach who runs into that
 * is exactly the one who knows where everything is already.
 */
export function Sidebar({ locale, onSignOut }: { locale: Locale; onSignOut: () => void }) {
  const t = useTranslations();
  const { user, orgId, switchOrg } = useSession();
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState(false);

  // Read once on mount rather than during render: storage throws in a private
  // window, and a nav that fails to draw is worse than one that forgets.
  useEffect(() => {
    try {
      setCollapsed(window.localStorage.getItem(STORE_KEY) === '1');
    } catch {
      /* no stored preference is a fine preference */
    }
  }, []);

  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(STORE_KEY, next ? '1' : '0');
      } catch {
        /* forgetting the choice is survivable */
      }
      return next;
    });
  };

  const activeOrg = user?.orgs.find((o) => o.id === orgId) ?? user?.orgs[0];

  return (
    <div
      className={`sticky top-0 hidden h-dvh shrink-0 flex-col border-e border-line bg-surface lg:flex ${
        collapsed ? 'w-14' : 'w-60'
      }`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {!collapsed ? (
        <div className="flex items-center gap-2 px-3 py-3">
          <Link
            to={localePath(locale, '/')}
            className="min-w-0 truncate font-condensed text-subhead font-bold tracking-tight"
          >
            {t('app.name')}
          </Link>
        </div>
      ) : null}

      {!collapsed && user && user.orgs.length > 1 ? (
        <label className="px-3 pb-3">
          <span className="sr-only">{t('nav.team')}</span>
          <select
            value={orgId ?? ''}
            onChange={(e) => switchOrg(e.target.value)}
            className="w-full truncate rounded-(--radius-control) border border-line bg-surface px-2 py-2 text-meta hover:border-line-strong focus:border-ink focus:outline-none"
          >
            {user.orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      ) : !collapsed && activeOrg ? (
        <p className="truncate px-3 pb-3 text-meta text-ink-faint">{activeOrg.name}</p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        <nav aria-label={t('nav.primary')}>
          {/* Asked from wherever the coach is standing, so it sits outside the
              phases of the week rather than inside one of them. */}
          <ul className="px-2 pb-2">
            <li>
              <SidebarLink item={ASSISTANT} locale={locale} collapsed={collapsed} />
            </li>
          </ul>

          {GROUPS.map((group) => (
            <div key={group.key} className="pt-1">
              {collapsed ? (
                <div aria-hidden="true" className="mx-3 my-2 border-t border-line" />
              ) : (
                <h2 className="px-3 pt-2 pb-1 font-condensed text-meta font-semibold text-ink-faint">
                  {t(`nav.group.${group.key}`)}
                </h2>
              )}
              <ul className="grid gap-0.5 px-2">
                {group.items.map((item) => (
                  <li key={item.key}>
                    <SidebarLink item={item} locale={locale} collapsed={collapsed} />
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {user?.isPlatformAdmin ? (
            <ul className="px-2 pt-3">
              <li>
                <Link
                  to={localePath(locale, '/admin')}
                  title={collapsed ? t('nav.admin') : undefined}
                  className={`flex min-h-11 items-center gap-3 rounded-(--radius-control) border-s-2 border-signal px-2.5 text-meta font-medium text-ink-muted hover:bg-surface-sunken hover:text-ink ${
                    collapsed ? 'justify-center' : ''
                  }`}
                >
                  <ShieldCheck aria-hidden="true" className="size-5 shrink-0" />
                  {!collapsed ? (
                    <span className="min-w-0 truncate">{t('nav.admin')}</span>
                  ) : (
                    <span className="sr-only">{t('nav.admin')}</span>
                  )}
                </Link>
              </li>
            </ul>
          ) : null}
        </nav>
      </div>

      <div className="border-t border-line p-2">
        {!collapsed ? (
          <nav aria-label={t('nav.language')} className="flex gap-0.5 pb-2">
            {LOCALES.map((l) => (
              <Link
                key={l}
                to={swapLocale(pathname, l)}
                aria-current={l === locale ? 'page' : undefined}
                className={`tabular grid min-h-11 flex-1 place-items-center rounded-(--radius-control) font-condensed text-meta font-semibold uppercase transition-colors ${
                  l === locale ? 'bg-ink text-paper' : 'text-ink-faint hover:bg-surface-sunken hover:text-ink'
                }`}
              >
                {l}
              </Link>
            ))}
          </nav>
        ) : null}

        <button
          type="button"
          onClick={toggle}
          aria-pressed={collapsed}
          title={collapsed ? t('nav.expand') : t('nav.collapse')}
          className="flex min-h-11 w-full items-center gap-3 rounded-(--radius-control) px-2.5 text-meta text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
        >
          {collapsed ? (
            <PanelLeftOpen aria-hidden="true" className="size-5 shrink-0" />
          ) : (
            <PanelLeftClose aria-hidden="true" className="size-5 shrink-0" />
          )}
          {!collapsed ? <span className="min-w-0 truncate">{t('nav.collapse')}</span> : null}
          <span className="sr-only">{collapsed ? t('nav.expand') : t('nav.collapse')}</span>
        </button>

        {!collapsed && user ? (
          <p className="truncate px-2.5 pt-2 text-meta text-ink-faint">{user.email}</p>
        ) : null}

        <button
          type="button"
          onClick={onSignOut}
          title={collapsed ? t('nav.signOut') : undefined}
          className={`mt-1 flex min-h-11 w-full items-center gap-3 rounded-(--radius-control) px-2.5 text-start text-meta text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <LogOut aria-hidden="true" className="size-5 shrink-0" />
          {!collapsed ? <span className="min-w-0 truncate">{t('nav.signOut')}</span> : null}
          <span className="sr-only">{t('nav.signOut')}</span>
        </button>
      </div>
    </div>
  );
}

function SidebarLink({
  item,
  locale,
  collapsed,
}: {
  item: NavItem;
  locale: Locale;
  collapsed: boolean;
}) {
  const t = useTranslations();
  const label = t(`nav.${item.key}`);
  const Icon = NAV_ICONS[item.key];

  return (
    <NavLink
      to={localePath(locale, item.to)}
      end={item.end}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `flex min-h-11 items-center gap-3 rounded-(--radius-control) px-2.5 transition-colors ${
          collapsed ? 'justify-center' : ''
        } ${
          isActive
            ? 'bg-signal-soft font-semibold text-ink'
            : 'text-ink-muted hover:bg-surface-sunken hover:text-ink'
        }`
      }
    >
      <Icon aria-hidden="true" className="size-5 shrink-0" />
      {!collapsed ? <span className="min-w-0 truncate">{label}</span> : null}
      {collapsed ? <span className="sr-only">{label}</span> : null}
    </NavLink>
  );
}
